import { useState } from "react";
import { Link } from "react-router-dom";
import { Award } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtDate, num } from "../lib/format";
import { ListPageShell } from "../components/shell/ListPageShell";
import { DataTable, type Column } from "../components/DataTable";
import { getNextPageParam } from "../lib/infinite-list";

interface ReputationRow {
  userId: string;
  email: string;
  displayName: string | null;
  reputationScore: number;
  tierLabel: string;
  activeSlotLimit: number;
  activePlans: number;
  completedPlans: number;
  onTimePayments: number;
  latePayments: number;
  defaults: number;
  lastEvaluatedAt: string | null;
}

interface ReputationPage {
  profiles: ReputationRow[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 25;

export function FlexxiReputation() {
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["flexxi", "reputation", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(PAGE_SIZE),
      });
      if (search) params.set("search", search);
      return api<ReputationPage>(`/admin/flexxi/reputation/profiles?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage.total, allPages.reduce((acc, p) => acc + p.profiles.length, 0), PAGE_SIZE),
  });

  const rows = query.data?.pages.flatMap((p) => p.profiles) ?? [];

  const columns: Column<ReputationRow>[] = [
    {
      key: "customer",
      header: "Customer",
      sortable: true,
      sortValue: (r) => r.displayName ?? r.email,
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{r.displayName ?? "—"}</p>
          <p className="truncate text-[11px] text-muted">{r.email}</p>
        </div>
      ),
    },
    {
      key: "score",
      header: "Score",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.reputationScore,
      render: (r) => <span className="font-display tabular-nums font-semibold text-brand-pink">{num(r.reputationScore)}</span>,
    },
    {
      key: "tier",
      header: "Tier",
      render: (r) => <span className="text-xs text-foreground">{r.tierLabel}</span>,
    },
    {
      key: "slots",
      header: "Slots",
      className: "text-right",
      render: (r) => (
        <span className="tabular-nums text-xs text-muted">
          {r.activePlans} / {r.activeSlotLimit} active
        </span>
      ),
    },
    {
      key: "onTime",
      header: "On-time",
      className: "text-right hideSm",
      sortable: true,
      sortValue: (r) => r.onTimePayments,
      render: (r) => <span className="tabular-nums text-xs">{num(r.onTimePayments)}</span>,
    },
    {
      key: "late",
      header: "Late",
      className: "text-right hideSm",
      sortable: true,
      sortValue: (r) => r.latePayments,
      render: (r) => <span className="tabular-nums text-xs">{num(r.latePayments)}</span>,
    },
    {
      key: "defaults",
      header: "Defaults",
      className: "text-right hideSm",
      sortable: true,
      sortValue: (r) => r.defaults,
      render: (r) => <span className="tabular-nums text-xs">{num(r.defaults)}</span>,
    },
    {
      key: "completed",
      header: "Completed",
      className: "text-right hideSm",
      sortable: true,
      sortValue: (r) => r.completedPlans,
      render: (r) => <span className="tabular-nums text-xs">{num(r.completedPlans)}</span>,
    },
    {
      key: "updated",
      header: "Updated",
      hideSm: true,
      render: (r) => (
        <span className="text-[11px] text-muted">{r.lastEvaluatedAt ? fmtDate(r.lastEvaluatedAt) : "—"}</span>
      ),
    },
    {
      key: "user",
      header: "",
      className: "text-right",
      render: (r) => (
        <Link to={`/users?record=${encodeURIComponent(r.userId)}`} className="text-xs font-semibold text-brand-pink hover:underline">
          User
        </Link>
      ),
    },
  ];

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.userId}
        loading={query.isLoading}
        entityLabel="Customer"
        exportFilename="flexxi-reputation"
        fillViewport
        toolbar={{
          search: {
            value: searchDraft,
            onChange: setSearchDraft,
            onSubmit: () => setSearch(searchDraft.trim()),
            placeholder: "Search by name or email…",
          },
          onRefresh: () => void query.refetch(),
        }}
        empty={{
          icon: Award,
          title: "No Flexxi reputation profiles yet",
          hint: "Scores appear after customers open instalment plans or make plan payments.",
        }}
        infiniteScroll={{
          hasNextPage: query.hasNextPage,
          isFetchingNextPage: query.isFetchingNextPage,
          onLoadMore: () => void query.fetchNextPage(),
        }}
      />
    </ListPageShell>
  );
}
